import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const InvoiceEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [invoiceData, setInvoiceData] = useState({
    service_description: '',
    hours: '',
    unit_price: '',
    bill_to_name: '',
    bill_to_phone: '',
    bill_to_email: '',
    due_date: '',
    service_start: '',
    service_end: '',
    service_start_time: '',
    service_end_time: '',
    recurring: false,
    weekly_day: '',
    payment_advisor_name: '',
    payment_advisor_contact: '',
    payment_advisor_email: '',
    include_payment_advisor: true
  });

  useEffect(() => {
    fetchInvoiceData();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/records/${id}`);
      const invoice = response.data;
      
      // Convert due date back to YYYY-MM-DD format for date input
      let dueDateInput = '';
      if (invoice.due_date) {
        try {
          // Parse formatted date like "Monday, 12/16/2024"
          const match = invoice.due_date.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
          if (match) {
            const [, month, day, year] = match;
            dueDateInput = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        } catch (e) {
          console.error('Error parsing due date:', e);
        }
      }
      
      setInvoiceData({
        service_description: invoice.service_description || '',
        hours: invoice.hours?.toString() || '',
        unit_price: invoice.unit_price?.toString() || '',
        bill_to_name: invoice.bill_to_name || '',
        bill_to_phone: invoice.bill_to_phone || '',
        bill_to_email: invoice.bill_to_email || '',
        due_date: dueDateInput,
        service_start: invoice.service_start || '',
        service_end: invoice.service_end || '',
        service_start_time: invoice.service_start_time || '',
        service_end_time: invoice.service_end_time || '',
        recurring: !!invoice.recurring,
        weekly_day: invoice.weekly_day || '',
        payment_advisor_name: invoice.payment_advisor_name || '',
        payment_advisor_contact: invoice.payment_advisor_contact || '',
        payment_advisor_email: invoice.payment_advisor_email || '',
        include_payment_advisor: !!(invoice.payment_advisor_name || invoice.payment_advisor_contact || invoice.payment_advisor_email)
      });
    } catch (err) {
      console.error('Failed to fetch invoice data:', err);
      setError('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      // Format the due date for the backend
      let formattedDueDate = invoiceData.due_date;
      if (invoiceData.due_date && invoiceData.due_date.includes('-')) {
        const [year, month, day] = invoiceData.due_date.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayOfWeek = dayNames[date.getDay()];
        formattedDueDate = `${dayOfWeek}, ${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      }

      // Build request data, only including non-empty values
      const requestData = { id };
      
      if (invoiceData.service_start) requestData.service_start = invoiceData.service_start;
      if (invoiceData.service_end) requestData.service_end = invoiceData.service_end;
      if (invoiceData.service_start_time) requestData.service_start_time = invoiceData.service_start_time;
      if (invoiceData.service_end_time) requestData.service_end_time = invoiceData.service_end_time;
      if (invoiceData.service_description) requestData.description = invoiceData.service_description;
      if (invoiceData.hours) requestData.hours = Number(invoiceData.hours);
      if (invoiceData.unit_price) requestData.unit_price = Number(invoiceData.unit_price);
      if (invoiceData.bill_to_name) requestData.bill_to_name = invoiceData.bill_to_name;
      if (invoiceData.bill_to_phone) requestData.bill_to_phone = invoiceData.bill_to_phone;
      if (invoiceData.bill_to_email) requestData.bill_to_email = invoiceData.bill_to_email;
      if (formattedDueDate) requestData.due_date = formattedDueDate;
      requestData.recurring = invoiceData.recurring;
      if (invoiceData.recurring && invoiceData.weekly_day) requestData.weekly_day = invoiceData.weekly_day;
      
      // Only include payment advisor info if checkbox is checked
      requestData.include_payment_advisor = invoiceData.include_payment_advisor;
      if (invoiceData.include_payment_advisor) {
        if (invoiceData.payment_advisor_name) requestData.payment_advisor_name = invoiceData.payment_advisor_name;
        if (invoiceData.payment_advisor_contact) requestData.payment_advisor_contact = invoiceData.payment_advisor_contact;
        if (invoiceData.payment_advisor_email) requestData.payment_advisor_email = invoiceData.payment_advisor_email;
      }

      // Update invoice in database
      await axios.put(`${API_BASE_URL}/billing/invoice/records/${id}`, requestData);
      
      // Regenerate PDF with updated data
      const response = await axios.post(`${API_BASE_URL}/billing/invoice`, requestData);
      
      if (response.data.success) {
        setSuccessMessage('Invoice updated and PDF regenerated successfully!');
        setTimeout(() => {
          navigate('/invoice-list');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to regenerate PDF');
      }
    } catch (err) {
      console.error('Failed to update invoice:', err);
      setError(err.response?.data?.message || 'Failed to update invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/invoice-list');
  };

  if (loading) {
    return <div className="container"><p>Loading invoice data...</p></div>;
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Invoice</h2>
        <button className="btn btn-outline-secondary" onClick={handleCancel}>
          Back to Invoice List
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col">
            <label className="form-label">Service From (Date)</label>
            <input
              type="date"
              className="form-control"
              name="service_start"
              value={invoiceData.service_start}
              onChange={handleInputChange}
            />
          </div>
          <div className="col">
            <label className="form-label">Service To (Date)</label>
            <input
              type="date"
              className="form-control"
              name="service_end"
              value={invoiceData.service_end}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col">
            <label className="form-label">Service From (Time)</label>
            <input
              type="time"
              className="form-control"
              name="service_start_time"
              value={invoiceData.service_start_time}
              onChange={handleInputChange}
            />
          </div>
          <div className="col">
            <label className="form-label">Service To (Time)</label>
            <input
              type="time"
              className="form-control"
              name="service_end_time"
              value={invoiceData.service_end_time}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Service Description</label>
          <textarea
            className="form-control"
            rows="3"
            name="service_description"
            value={invoiceData.service_description}
            onChange={handleInputChange}
          />
        </div>

        <div className="row mb-3">
          <div className="col">
            <label className="form-label">Hours</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="hours"
              value={invoiceData.hours}
              onChange={handleInputChange}
            />
          </div>
          <div className="col">
            <label className="form-label">Unit Price</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="unit_price"
              value={invoiceData.unit_price}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Bill To Name</label>
          <input
            type="text"
            className="form-control"
            name="bill_to_name"
            value={invoiceData.bill_to_name}
            onChange={handleInputChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Bill To Phone</label>
          <input
            type="text"
            className="form-control"
            name="bill_to_phone"
            value={invoiceData.bill_to_phone}
            onChange={handleInputChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Bill To Email</label>
          <input
            type="email"
            className="form-control"
            name="bill_to_email"
            value={invoiceData.bill_to_email}
            onChange={handleInputChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Due Date</label>
          <input
            type="date"
            className="form-control"
            name="due_date"
            value={invoiceData.due_date}
            onChange={handleInputChange}
          />
        </div>

        <div className="mb-3 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="recurring"
            id="recurringCheck"
            checked={invoiceData.recurring}
            onChange={handleInputChange}
          />
          <label className="form-check-label" htmlFor="recurringCheck">
            Recurring Invoice?
          </label>
        </div>

        {invoiceData.recurring && (
          <div className="mb-3">
            <label className="form-label">Recurring Day</label>
            <select
              className="form-select"
              name="weekly_day"
              value={invoiceData.weekly_day}
              onChange={handleInputChange}
            >
              <option value="">-- Select Day --</option>
              {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((day) => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-3 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="include_payment_advisor"
            id="includePaymentAdvisor"
            checked={invoiceData.include_payment_advisor}
            onChange={handleInputChange}
          />
          <label className="form-check-label" htmlFor="includePaymentAdvisor">
            Include Payment Advisor Information
          </label>
        </div>

        {invoiceData.include_payment_advisor && (
          <>
            <h5>Payment Advisor</h5>
            <div className="mb-3">
              <label className="form-label">Advisor Name</label>
              <input
                type="text"
                className="form-control"
                name="payment_advisor_name"
                value={invoiceData.payment_advisor_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Advisor Contact</label>
              <input
                type="text"
                className="form-control"
                name="payment_advisor_contact"
                value={invoiceData.payment_advisor_contact}
                onChange={handleInputChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Advisor Email</label>
              <input
                type="email"
                className="form-control"
                name="payment_advisor_email"
                value={invoiceData.payment_advisor_email}
                onChange={handleInputChange}
              />
            </div>
          </>
        )}

        <div className="d-flex gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Updating...' : 'Update Invoice & Regenerate PDF'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceEditPage;